import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DAY_NAMES, MONTH_NAMES } from '../types';
import {
  startOfMonth,
  startOfWeek,
  addDays,
  isSameDay,
  isSameMonth,
  isToday,
  addMonths,
} from '../utils';

type ZoomLevel = 'days' | 'months' | 'years';

const MONTH_LABELS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface MiniMonthProps {
  currentDate: Date;
  selectedDate: Date;
  weekStartDay: number;
  onSelectDate: (date: Date) => void;
}

export function MiniMonth({
  currentDate,
  selectedDate,
  weekStartDay,
  onSelectDate,
}: MiniMonthProps) {
  const [zoom, setZoom] = useState<ZoomLevel>('days');
  // Internal display month — independent from the main calendar's currentDate
  const [displayMonth, setDisplayMonth] = useState<Date>(startOfMonth(currentDate));
  const [viewYear, setViewYear] = useState(currentDate.getFullYear());

  // Sync display month when the parent navigates the main calendar
  // (e.g. toolbar chevrons, "Today" button, clicking a date in month view)
  const prevCurrentDate = useRef(currentDate);
  useEffect(() => {
    if (!isSameDay(prevCurrentDate.current, currentDate)) {
      setDisplayMonth(startOfMonth(currentDate));
      prevCurrentDate.current = currentDate;
    }
  }, [currentDate]);

  if (zoom === 'years') {
    return (
      <YearsView
        viewYear={viewYear}
        selectedYear={currentDate.getFullYear()}
        onSelectYear={(year) => {
          setViewYear(year);
          setZoom('months');
        }}
        onNavigate={(delta) => setViewYear((y) => y + delta)}
      />
    );
  }

  if (zoom === 'months') {
    return (
      <MonthsView
        viewYear={viewYear}
        selectedMonth={currentDate.getMonth()}
        selectedYear={currentDate.getFullYear()}
        onSelectMonth={(month) => {
          const d = new Date(viewYear, month, 1);
          setDisplayMonth(d);
          setZoom('days');
        }}
        onNavigate={(delta) => setViewYear((y) => y + delta)}
        onZoomOut={() => setZoom('years')}
      />
    );
  }

  return (
    <DaysView
      displayMonth={displayMonth}
      selectedDate={selectedDate}
      weekStartDay={weekStartDay}
      onSelectDate={(day) => {
        onSelectDate(day);
        setDisplayMonth(startOfMonth(day));
      }}
      onPrevMonth={() => setDisplayMonth((d) => addMonths(d, -1))}
      onNextMonth={() => setDisplayMonth((d) => addMonths(d, 1))}
      onZoomOut={() => {
        setViewYear(displayMonth.getFullYear());
        setZoom('months');
      }}
    />
  );
}

/* ========================================================================== */
/* Days View                                                                   */
/* ========================================================================== */

function DaysView({
  displayMonth,
  selectedDate,
  weekStartDay,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
  onZoomOut,
}: {
  displayMonth: Date;
  selectedDate: Date;
  weekStartDay: number;
  onSelectDate: (date: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onZoomOut: () => void;
}) {
  const monthStart = startOfMonth(displayMonth);
  const gridStart = startOfWeek(monthStart, weekStartDay);

  const days: Date[] = [];
  let d = new Date(gridStart);
  for (let i = 0; i < 42; i++) {
    days.push(new Date(d));
    d = addDays(d, 1);
  }

  const dayHeaders = [];
  for (let i = 0; i < 7; i++) {
    const dayIndex = (weekStartDay + i) % 7;
    dayHeaders.push(DAY_NAMES[dayIndex]);
  }

  // Only show the selected circle if the user's selected date is in this month
  const showSelected = isSameMonth(selectedDate, displayMonth);
  // Whether today is in the displayed month
  const now = new Date();
  const todayInMonth = isSameMonth(now, displayMonth);

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-2">
        <button
          aria-label="Previous month"
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={onPrevMonth}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          className="text-xs font-medium hover:text-primary transition-colors"
          onClick={onZoomOut}
        >
          {MONTH_NAMES[displayMonth.getMonth()]} {displayMonth.getFullYear()}
        </button>
        <button
          aria-label="Next month"
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={onNextMonth}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0">
        {dayHeaders.map((name) => (
          <div
            key={name}
            className="text-center text-[10px] text-muted-foreground font-medium py-1"
          >
            {name[0]}
          </div>
        ))}

        {days.map((day, i) => {
          const inMonth = isSameMonth(day, displayMonth);
          const today = isToday(day);
          const selected = showSelected && isSameDay(day, selectedDate);
          // Highlight today with the circle when it's in this month and nothing is explicitly selected,
          // or when selectedDate IS today
          const highlightToday = todayInMonth && today && !selected;

          return (
            <button
              key={i}
              onClick={() => onSelectDate(day)}
              className={`
                w-7 h-7 flex items-center justify-center text-[11px] rounded-full transition-colors
                ${!inMonth ? 'text-muted-foreground/40' : ''}
                ${highlightToday ? 'text-primary font-bold ring-1 ring-primary' : ''}
                ${selected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}
              `}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Months View — 4×3 grid of months for a given year                          */
/* ========================================================================== */

function MonthsView({
  viewYear,
  selectedMonth,
  selectedYear,
  onSelectMonth,
  onNavigate,
  onZoomOut,
}: {
  viewYear: number;
  selectedMonth: number;
  selectedYear: number;
  onSelectMonth: (month: number) => void;
  onNavigate: (delta: number) => void;
  onZoomOut: () => void;
}) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-2">
        <button
          aria-label="Previous year"
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={() => onNavigate(-1)}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          className="text-xs font-medium hover:text-primary transition-colors"
          onClick={onZoomOut}
        >
          {viewYear}
        </button>
        <button
          aria-label="Next year"
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={() => onNavigate(1)}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-1">
        {MONTH_LABELS_SHORT.map((label, i) => {
          const isCurrent = viewYear === currentYear && i === currentMonth;
          const isSelected = viewYear === selectedYear && i === selectedMonth;

          return (
            <button
              key={i}
              onClick={() => onSelectMonth(i)}
              className={`
                h-9 flex items-center justify-center text-[11px] rounded-md transition-colors
                ${isCurrent && !isSelected ? 'text-primary font-bold ring-1 ring-primary' : ''}
                ${isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}
              `}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Years View — 4×3 grid of years for a decade                                */
/* ========================================================================== */

function YearsView({
  viewYear,
  selectedYear,
  onSelectYear,
  onNavigate,
}: {
  viewYear: number;
  selectedYear: number;
  onSelectYear: (year: number) => void;
  onNavigate: (delta: number) => void;
}) {
  const decadeStart = viewYear - (viewYear % 10) - 1;
  const years = Array.from({ length: 12 }, (_, i) => decadeStart + i);
  const nowYear = new Date().getFullYear();
  const rangeStart = decadeStart + 1;
  const rangeEnd = decadeStart + 10;

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-2">
        <button
          aria-label="Previous decade"
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={() => onNavigate(-10)}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="text-xs font-medium">
          {rangeStart}–{rangeEnd}
        </span>
        <button
          aria-label="Next decade"
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={() => onNavigate(10)}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-1">
        {years.map((year) => {
          const inDecade = year >= rangeStart && year <= rangeEnd;
          const isCurrent = year === nowYear;
          const isSelected = year === selectedYear;

          return (
            <button
              key={year}
              onClick={() => onSelectYear(year)}
              className={`
                h-9 flex items-center justify-center text-[11px] rounded-md transition-colors
                ${!inDecade ? 'text-muted-foreground/40' : ''}
                ${isCurrent && !isSelected ? 'text-primary font-bold ring-1 ring-primary' : ''}
                ${isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}
              `}
            >
              {year}
            </button>
          );
        })}
      </div>
    </div>
  );
}
